import { validation } from './Validation';
import { ValidationInstance } from './Validation/validation-instance';
import { setAttributes } from '../util';

const errorContainerClass = 'form-errors';
const errorNodeClass = 'invalid-feedback';
const inputGroupClass = 'form-group';

/**
 * Returns the container where an inputGroup places it's errors, evaluating
 * `data-validation-placement` if set.
 * @param {HTMLElement} inputGroup
 * @returns {HTMLElement}}
 */
const getErrorNodePosition = (inputGroup) => {
    let errorNodePosition = inputGroup;
    if (inputGroup.hasAttribute('data-validation-placement')) {
        const selector = inputGroup.getAttribute('data-validation-placement').split(' ')[1];
        const parent = inputGroup.closest(selector);
        if (parent) errorNodePosition = parent.parentNode;
    }
    return errorNodePosition;
};

/**
 * Insert a node according to a string like `before #element` or `after .selector`
 * on the closest parent matching the selector.
 * @param {HTMLElement} entry
 * @param {HTMLElement} node
 * @param {string} positionString
 */
const insertPositional = (entry, node, positionString) => {
    const positionStrings = positionString.split(',').map(p => p.trim());
    for (let i = 0; i < positionStrings.length; i++) {
        const [ order, selector ] = positionStrings[i].split(' ');
        let pivot;
        if (selector === 'this') {
            pivot = entry;
        } else {
            pivot = entry.closest(selector);
            if (!pivot) pivot = entry.querySelector(selector);
        }
        if (!pivot) continue;
        switch (order) {
            case 'before': pivot.parentNode.insertBefore(node, pivot); break;
            case 'after': pivot.parentNode.insertBefore(node, pivot.nextSibling); break;
            case 'in': pivot.appendChild(node); break;
        }
        break;
    }
};

/**
 * @param {HTMLFormElement} form
 * @returns {HTMLElement|null}
 */
const findErrorContainer = form => {
    const container = form.querySelector('.form-error-container');
    if (container === null) return null;
    return container.querySelector('.' + errorContainerClass);
};

/**
 * @param {HTMLFormElement} form
 * @returns {HTMLUListElement}
 */
const getErrorContainer = form => {
    const container = form.querySelector('.form-error-container');
    if (container && container.children && container.children.length > 0) {
        return container.querySelector('ul');
    }
    const wrapper = document.createElement('DIV');
    wrapper.classList.add(errorContainerClass);
    const heading = document.createElement('H2');
    heading.classList.add('copy-h2');
    heading.setAttribute('tabindex', '-1');
    // TODO no magic strings
    heading.innerText = 'Korrigieren Sie folgende Fehler im Formular:';
    wrapper.appendChild(heading);
    const list = document.createElement('UL');
    list.classList.add('copy-list');
    list.classList.add('copy-list--ul');
    wrapper.appendChild(list);
    container.appendChild(wrapper);
    setTimeout(() => {
        heading.focus();
    }, 0);

    return list;
};

/**
 * Inserts the error-handle relative to the inputs' position among other inputs
 * @param {HTMLElement} errorContainer
 * @param {HTMLElement} errorHandle
 * @param {HTMLElement} input
 */
const insertErrorHandle = (errorContainer, errorHandle, input) => {
    if (!errorHandle) {
        return;
    }
    let handle = null;
    let entry = null;
    let insertBefore = false;
    for (let i = 0; i < errorContainer.children.length; i++) {
        entry = errorContainer.children[i];
        handle = entry.querySelector('a');
        if (!handle)
            continue;
        const other = document.querySelector(handle.getAttribute('href'));
        if (!other)
            continue;
        if (input.compareDocumentPosition(other) === Node.DOCUMENT_POSITION_FOLLOWING) {
            insertBefore = true;
            break;
        }
    }
    if (insertBefore) {
        errorContainer.insertBefore(errorHandle, entry);
    } else {
        errorContainer.appendChild(errorHandle);
    }
};

/**
 * Create a clickable error handle if necessary
 * @param {HTMLInputElement} input
 * @param {string} type
 * @param {string|null} message
 * @returns {boolean|HTMLElement}
 */
const setErrorHandle = (input, type, message) => {
    const elId = ValidationInstance.getIdentifier(input);
    const errorId = `error-list-${elId}_${type}`;
    let a = document.querySelector(`#${errorId}`);
    let r = false;
    let li;
    let h3;
    if (a === null) {
        li = document.createElement('LI');
        h3 = document.createElement('P');
        li.classList.add('error-list__item');
        h3.classList.add('copy-text');
        a = document.createElement('A');
        setAttributes(a, {
            'id': errorId,
            'href': `#${elId}`,
            'tabindex': 0,
        });
        h3.appendChild(a);
        li.appendChild(h3);
        a.addEventListener('click', e => {
            e.preventDefault();
            if (!e.currentTarget.parentElement.classList.contains('error-list__item--done')) {
                input.focus();
            }
        });
        a.addEventListener('keydown', e => {
            if (e.keyCode === 13) {
                e.preventDefault();
                if (!e.currentTarget.parentElement.classList.contains('error-list__item--done')) {
                    input.focus();
                }
            }
        });
        r = li;
    } else {
        li = a.closest('li');
    }
    if (message !== null) {
        a.innerHTML = message;
        li.classList.remove('error-list__item--done');
        a.setAttribute('tabindex', '0');
    } else {
        li.classList.add('error-list__item--done');
        a.setAttribute('tabindex', '-1');
    }
    return r;
};

/**
 * @param {HTMLElement} inputGroup
 * @param {HTMLInputElement} input
 * @param {HTMLElement} node
 */
const insertErrorNode = (inputGroup, input, node) => {
    if (input.type === 'radio' && inputGroup.hasAttribute('data-validation-placement')) {
        return insertPositional(inputGroup, node, inputGroup.getAttribute('data-validation-placement'));
    }
    insertPositional(inputGroup, node, 'before .form-text, in this');
};

/**
 * @param {HTMLElement} inputGroup
 * @param {HTMLInputElement} input
 * @param {string} validatorType
 * @param {boolean} create
 * @returns {HTMLElement}
 */
const getErrorNode = (inputGroup, input, validatorType, create = true) => {
    const id = `${ValidationInstance.getIdentifier(input)}_${validatorType}`;
    let node = document.querySelector('#' + id);
    if (node === null && create) {
        node = document.createElement('SPAN');
        node.id = id;
        node.classList.add(errorNodeClass);
        insertErrorNode(inputGroup, input, node);
    }
    return node;
};

/**
 * @param {string} label The field's label
 * @param {string} validatorType The validator's name as registered in Validation/index.js
 * @param {object|null} args Additional arguments provided by some validators
 * @returns {string}
 */
const getErrorMessage = (label, validatorType, args) => {
    return ({
        'required': label => `"${label}" ist ein Pflichtfeld`,
        'email': label => `"${label}" ist keine gültige E-Mail Adresse`,
        'number': label => `"${label}" darf nur Ziffern enthalten`,
        'pattern': label => `"${label}" hat nicht das richtige Format`,
        'string-length': (label, args) => {
            if (args.min === undefined) {
                return `"${label}" darf maximal ${args.max} Zeichen enthalten.`;
            }
            if (args.max === undefined) {
                return `"${label}" darf mindestens ${args.min} Zeichen enthalten.`;
            }
            if (args.max === args.min) {
                return `"${label}" muss genau ${args.min} Zeichen lang sein`;
            }
            return `"${label}" muss zwischen ${args.min} und ${args.max} Zeichen lang sein`;
        },
    }[validatorType] || (() => `${label}: ${validatorType}`))(label, args);
};

/**
 * @param {HTMLElement} inputGroup
 * @returns {string}
 */
const getErrorNodeIds = (inputGroup) => {
    return Array.from(getErrorNodePosition(inputGroup).querySelectorAll('.' + errorNodeClass))
        .map(n => n.id)
        .filter(n => document.getElementById(n) !== null)
        .join(' ');
};

/**
 * @param {HTMLFormElement} form
 * @param {ValidationInstance} vi
 * @returns {function(HTMLElement, string, object?)}
 */
const handleValidationError = (form, vi) => (inputGroup, validatorType, args) => {
    const input = vi.getInput(inputGroup);
    const errorContainer = getErrorContainer(form);
    const message = getErrorMessage(ValidationInstance.getLabel(inputGroup), validatorType, args);
    const errorHandle = setErrorHandle(vi.getInput(inputGroup), validatorType, message);
    insertErrorHandle(errorContainer, errorHandle, input);
    const errorNode = getErrorNode(inputGroup, input, validatorType, true);
    errorNode.classList.add('help-block');
    errorNode.innerText = message;
    input.setAttribute('aria-invalid', 'true');
    input.setAttribute('aria-describedby', getErrorNodeIds(inputGroup));
    inputGroup.classList.add(inputGroupClass + '--error');
};

/**
 * @param {ValidationInstance} vi
 * @returns {function(HTMLElement, string)}
 */
const handleValidationSuccess = vi => (inputGroup, validatorType) => {
    const input = vi.getInput(inputGroup);
    setErrorHandle(input, validatorType, null);
    const errorNode = getErrorNode(inputGroup, input, validatorType);
    if (errorNode) {
        errorNode.remove();
    }
    const errorNodeIds = getErrorNodeIds(inputGroup);
    if (errorNodeIds.length > 0) {
        input.setAttribute('aria-describedby', errorNodeIds);
        inputGroup.classList.remove(inputGroupClass + '--valid');
    } else {
        input.removeAttribute('aria-invalid');
        input.removeAttribute('aria-describedby');
        inputGroup.classList.add(inputGroupClass + '--valid');
        inputGroup.classList.remove(inputGroupClass + '--error');
    }
};

/**
 * @param {HTMLFormElement} form
 * @param {ValidationInstance} vi
 * @returns {function()}
 */
const handleClearValidationErrors = (form, vi) => () => {
    const formErrorsContainer = findErrorContainer(form);
    if (formErrorsContainer) {
        formErrorsContainer.remove();
    }
    Array.from(vi.inputGroups).forEach(g => vi.getInput(g).removeAttribute('aria-invalid'));
    Array.from(form.querySelectorAll('.' + errorNodeClass)).forEach(c => c.remove());
};

/**
 * @param {HTMLFormElement} form
 * @returns {function(boolean)}
 */
const handleSubmit = form => result => {
    const formErrorsContainer = findErrorContainer(form);
    if (formErrorsContainer === null) {
        return;
    }
    if (!result) {
        formErrorsContainer.classList.add(errorContainerClass + '--form-submitted');
        const heading = formErrorsContainer.querySelector('.copy-h2');
        if (heading) {
            heading.focus();
        }
    } else {
        formErrorsContainer.classList.remove(errorContainerClass + '--form-submitted');
    }
};


Array.from(document.forms)
    .filter(form => !form.hasAttribute('novalidate'))
    .forEach(/** @type {HTMLFormElement} */form => {
        const v = validation.init(form, {
            groupSelector: '.' + inputGroupClass,
            onValidationError: (inputGroup, validatorType, args) =>
                handleValidationError(form, v)(inputGroup, validatorType, args),
            onValidationSuccess: (inputGroup, validatorType, args) =>
                handleValidationSuccess(v)(inputGroup, validatorType, args),
            onSubmit: handleSubmit(form),
            onClearValidationErrors: () =>
                handleClearValidationErrors(form, v)(),
        });
        // for debugging
        // window.formValidator = v;
    });
